import React, { useContext, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import Calendar from 'react-calendar';
import moment from 'moment';
import * as styles from './create-reservation.styles';
import { IsMobileContext } from '../../../core/context/is-mobile';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import Column from '../../../core/components/column/column.component';
import { useAgendaEntriesForUserByMonth } from '../hooks/agenda-entry.hooks';
import type AgendaEntry from '../models/agenda-entry';
import ActivityIndicator from '../../../core/components/activity-indicator/activity-indicator.component';
import { Center } from '../../../core/components/center/center.component';
import StyleConstants from '../../../core/styles.constants';
import type PermittedUser from '../../offices/models/permitted-user';

interface Props {
  readonly showModal: boolean;
  readonly handleClose: () => void;
  readonly user: PermittedUser;
  readonly officeId: string;
  readonly onDatesSelected: (dates: Date[]) => void;
}

const expandDateRange = (start: Date, end: Date): Date[] => {
  const dates: Date[] = [];
  const current = new Date(start);
  while (current <= end) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
};

const MonthlyReservationOverviewModal = ({ showModal, handleClose, user, officeId, onDatesSelected }: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t, i18n } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const agendaEntriesState = useAgendaEntriesForUserByMonth(officeId, user.email, selectedDate);
  const [agendaEntries, setAgendaEntries] = useState<AgendaEntry[]>([]);
  const [selectionMode, setSelectionMode] = useState<'single' | 'range'>('single');
  const [dateRange, setDateRange] = useState<[Date, Date] | null>(null);

  const months = [
    t('january'),
    t('february'),
    t('march'),
    t('april'),
    t('may'),
    t('june'),
    t('july'),
    t('august'),
    t('september'),
    t('october'),
    t('november'),
    t('december'),
  ];

  useEffect(() => {
    setAgendaEntries(agendaEntriesState.userAgendaEntries ?? []);
  }, [agendaEntriesState.userAgendaEntries]);

  const didFetchAgendaEntriesForUser = (): boolean => {
    if (agendaEntriesState.userAgendaEntries == null) {
      return false;
    }

    if (agendaEntriesState.userAgendaEntries.length === 0) {
      return true;
    }

    return true;
  };

  const dateIsWithinMonth = (date: Date): boolean => date.getMonth() === selectedDate.getMonth();

  const existingAgendaEntryOnDate = (date: Date): boolean => {
    const dateToCheck = moment(date);
    return agendaEntries?.some((agendaEntry) => dateToCheck.isSame(agendaEntry.date, 'day'));
  };

  return (
    <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleClose} style={styles.modalStyleDatePicker(isMobile, 500)}>
      <Column className={style(styles.modalBodyDatePicker(isMobile))}>
        <span className={style(styles.columnHeaderDatePicker(isMobile, 8))}>
          {t('employee')}: {user.firstname} {user.lastname}
        </span>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <button
            type="button"
            className={style(styles.selectionModeButton(selectionMode === 'single'))}
            onClick={(): void => {
              setSelectionMode('single');
              setDateRange(null);
            }}
          >
            {t('calendar-single-date')}
          </button>
          <button
            type="button"
            className={style(styles.selectionModeButton(selectionMode === 'range'))}
            onClick={(): void => {
              setSelectionMode('range');
              setDateRange(null);
            }}
          >
            {t('calendar-date-range')}
          </button>
        </div>
        {!didFetchAgendaEntriesForUser() ? (
          <Center>
            <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
          </Center>
        ) : (
          <>
            <div className={style(styles.bodyText)}>
              <Calendar
                className={style(styles.calendar)}
                minDetail="month"
                minDate={new Date()}
                locale={i18n.language}
                selectRange={selectionMode === 'range'}
                onChange={(value): void => {
                  if (selectionMode === 'range' && Array.isArray(value) && value[0] != null && value[1] != null) {
                    setDateRange([value[0] as Date, value[1] as Date]);
                  }
                }}
                onActiveStartDateChange={(value): void => {
                  if (value.activeStartDate === null) {
                    return;
                  }

                  value.activeStartDate.setDate(value.activeStartDate.getDate() + 1);
                  setSelectedDate(value.activeStartDate);
                }}
                onClickDay={(value): void => {
                  if (selectionMode === 'single') {
                    onDatesSelected([value]);
                  }
                }}
                tileClassName={({ date }): string => {
                  const isDateToday = moment().isSame(date, 'day');

                  if (!dateIsWithinMonth(date)) {
                    return style(styles.disabledCalendarCell);
                  }
                  if (!existingAgendaEntryOnDate(date) && isDateToday) {
                    return style(styles.todayCalendarCell);
                  }
                  if (existingAgendaEntryOnDate(date) && isDateToday) {
                    return 'existing_entry_today_calendar_cell';
                  }
                  if (existingAgendaEntryOnDate(date) && !isDateToday) {
                    return 'existing_entry_calendar_cell';
                  }

                  return style(styles.defaultCalendarCell);
                }}
              />
              <div className={style(styles.horizontalDivider)} />
              <span className={style(styles.bodyText)}>
                {t('total-presence')} {months[selectedDate.getMonth()]} {selectedDate.getFullYear()}:{' '}
                {agendaEntries?.filter((agendaEntry) => new Date(agendaEntry.date).getMonth() === selectedDate.getMonth()).length}{' '}
                {t('day(s)')}
              </span>
            </div>
            <div className={style(styles.horizontalDivider)} />
          </>
        )}
        <Column itemSpacing={8}>
          {selectionMode === 'range' && dateRange != null && (
            <div className={style(styles.button)}>
              <PrimaryButton
                fullWidth
                onClick={(): void => {
                  const dates = expandDateRange(dateRange[0], dateRange[1]);
                  onDatesSelected(dates);
                }}
              >
                {`${t('book-selected-dates')} (${expandDateRange(dateRange[0], dateRange[1]).length} ${t('day(s)')})`}
              </PrimaryButton>
            </div>
          )}
          <div className={style(styles.button)}>
            <PrimaryButton fullWidth onClick={handleClose}>
              {t('close')}
            </PrimaryButton>
          </div>
        </Column>
      </Column>
    </Modal>
  );
};

export default MonthlyReservationOverviewModal;
